import * as R from 'ramda';
import React, { useState, useCallback, useEffect } from 'react';
// helpers/constants
import * as G from '../../helpers';
// utilities
import { sendRequestWithQSParamsSerializer } from '../../utilities/http';
//////////////////////////////////////////////////

export function useAsyncFile({
  showMsg,
  closeModal,
  openLoader,
  asyncMethod,
  closeLoader,
  asyncOptions,
  asyncEndpoint,
  asyncShouldCloseModal,
}) {
  const [asyncInitialData, setAsyncInitialData] = useState({ data: null, error: false, loading: true });

  const setLoading = useCallback(() => {
    setAsyncInitialData((prev) => ({ ...prev, loading: true }));
  }, []);

  const getFileRequest = useCallback(
    async (endpoint, requestOptions) => {
      if (G.isFunction(openLoader)) openLoader({ showDimmer: true });

      try {
        const options = R.or(requestOptions, asyncOptions);

        const res = await sendRequestWithQSParamsSerializer(
          R.or(asyncMethod, 'get'),
          R.or(endpoint, asyncEndpoint),
          options,
        );

        const { data, status } = res;

        if (G.isResponseSuccess(status) && G.isNotNil(data)) {
          const blob = new Blob(
            [data],
            { type: R.pathOr('application/pdf', ['headers', 'content-type'], res) },
          );

          setAsyncInitialData({ data: blob, error: false, loading: false });
        } else {
          setAsyncInitialData({ data: null, error: true, loading: false });

          G.handleFailResponseSimple(res, showMsg, 'withAsyncFile -> getFileRequest');
        }
      } catch (error) {
        G.handleException(error, 'withAsyncFile -> getFileRequest');
      } finally {
        if (G.isFunction(closeLoader)) closeLoader();

        if (asyncShouldCloseModal && G.isFunction(closeModal)) closeModal();
      }
    },
    [asyncMethod, asyncOptions, asyncEndpoint],
  );

  return { setLoading, getFileRequest, asyncInitialData };
}

const withAsyncFile = (Component) => (props) => {
  const initialData = useAsyncFile(props);

  return <Component {...props} {...initialData} />;
};

const withAsyncFileOnDidMount = (Component) => (props) => {
  const initialData = useAsyncFile(props);

  const { getFileRequest } = initialData;

  useEffect(() => {
    getFileRequest();
  }, []);

  return <Component {...props} {...initialData} />;
};

export {
  withAsyncFile,
  withAsyncFileOnDidMount,
};
